/**
 * Preprocesses text to convert non-standard math delimiters to standard LaTeX format
 * that remark-math can parse.
 *
 * Handles:
 * - Display math: [ ... ] on separate lines → $$ ... $$
 * - Inline math: {...} containing LaTeX → $...$
 * - Inline math: (...) containing LaTeX with = or operators → $...$
 */

/**
 * Convert display math blocks from [ ... ] format to $$ ... $$ format
 * Handles various formats:
 * - [ on its own line, content, ] on its own line
 * - [ on its own line, content with ] at the end
 */
function convertDisplayMath(text) {
  // Pattern 1: [ on its own line, content (possibly multiline), ] on its own line or end
  // This handles the format where [ and ] are on separate lines
  let result = text.replace(
    /(?:^|\n)\s*\[\s*\n([\s\S]*?)\n?\s*\]\s*(?=\n|$)/g,
    (match, mathContent) => {
      const trimmed = mathContent.trim();
      // Only convert if it looks like math (contains LaTeX commands)
      if (containsLatexCommands(trimmed)) {
        return '\n$$\n' + trimmed + '\n$$\n';
      }
      return match;
    }
  );

  // Pattern 2: Handle [ content ] where [ is at line start and content has LaTeX
  result = result.replace(
    /(?:^|\n)\s*\[\s*\n?([\s\S]*?)\s*\](?=\s*\n|$)/g,
    (match, mathContent) => {
      const trimmed = mathContent.trim();
      if (containsLatexCommands(trimmed)) {
        return '\n$$\n' + trimmed + '\n$$\n';
      }
      return match;
    }
  );

  return result;
}

/**
 * Check if content contains LaTeX commands (backslash followed by letters)
 */
function containsLatexCommands(content) {
  return /\\[a-zA-Z]+/.test(content);
}

/**
 * Check if content looks like LaTeX math (contains math operators/commands)
 */
function looksLikeMath(content) {
  // Must contain at least one LaTeX command or math operator pattern
  const mathPatterns = [
    /\\[a-zA-Z]+/,        // LaTeX commands like \frac, \int, \sqrt, \le, \ge
    /\^[\{\d]/,           // Superscripts like ^2 or ^{
    /_[\{\da-z]/,         // Subscripts like _n or _{
    /\\le|\\ge|\\neq|\\leq|\\geq/, // Comparison operators
    /\\cdot|\\times|\\div/, // Math operators
    /\\pm|\\mp/,          // Plus/minus
    /\\infty|\\pi|\\theta|\\alpha|\\beta|\\gamma|\\delta/, // Greek and special
    /\\sum|\\prod|\\lim/, // Big operators
    /\\sin|\\cos|\\tan|\\log|\\ln/, // Functions
  ];

  return mathPatterns.some(pattern => pattern.test(content));
}

/**
 * Check if this is likely a LaTeX command argument by looking at preceding context
 */
function isLikelyLatexArg(text, position) {
  const preceding = text.slice(Math.max(0, position - 30), position);
  // Check if preceded by a LaTeX command (with optional spaces/braces)
  return /\\[a-zA-Z]+\s*(\{[^}]*\}\s*)*$/.test(preceding);
}

/**
 * Convert inline math from {...} format to $...$ format
 * Only converts if content looks like LaTeX math
 */
function convertInlineMath(text) {
  const result = [];
  let i = 0;

  while (i < text.length) {
    if (text[i] === '{') {
      // Find the matching closing brace, accounting for nesting
      let depth = 1;
      let j = i + 1;

      while (j < text.length && depth > 0) {
        if (text[j] === '{') depth++;
        else if (text[j] === '}') depth--;
        j++;
      }

      if (depth === 0) {
        const content = text.slice(i + 1, j - 1);

        // Check if this is NOT a LaTeX command argument and looks like math
        if (!isLikelyLatexArg(text, i) && looksLikeMath(content)) {
          result.push('$' + content + '$');
          i = j;
          continue;
        }
      }
    }

    // Also handle (...) that contains LaTeX math (less common but possible)
    if (text[i] === '(' && i > 0) {
      // Look ahead to find matching )
      let j = i + 1;
      let depth = 1;
      while (j < text.length && depth > 0) {
        if (text[j] === '(') depth++;
        else if (text[j] === ')') depth--;
        j++;
      }

      if (depth === 0) {
        const content = text.slice(i + 1, j - 1);
        // Only convert if it contains backslash commands and looks like pure math
        // (avoid converting things like "(see figure 1)")
        if (/^[^a-zA-Z]*\\[a-zA-Z]/.test(content) && looksLikeMath(content)) {
          // Check it's not preceded by a word (like "function(x)")
          const precChar = text[i - 1];
          if (!/[a-zA-Z]/.test(precChar)) {
            result.push('$' + content + '$');
            i = j;
            continue;
          }
        }
      }
    }

    result.push(text[i]);
    i++;
  }

  return result.join('');
}

/**
 * Handle \( \) and \[ \] LaTeX delimiters
 */
function normalizeLatexDelimiters(text) {
  // Convert \( \) to $ $ for inline math
  text = text.replace(/\\\(/g, '$');
  text = text.replace(/\\\)/g, '$');

  // Convert \[ \] to $$ $$ for display math
  text = text.replace(/\\\[/g, '$$');
  text = text.replace(/\\\]/g, '$$');

  return text;
}

/**
 * Main preprocessing function to prepare text for remark-math
 */
export function preprocessMath(text) {
  if (!text) return text;

  // First normalize any existing LaTeX delimiters (\[ \] and \( \))
  let processed = normalizeLatexDelimiters(text);

  // Convert display math [ ... ] blocks
  processed = convertDisplayMath(processed);

  // Convert inline math {...}
  processed = convertInlineMath(processed);

  return processed;
}

export default preprocessMath;
